/**
 * discountCodes.js
 *
 * Shared discount-code math. Imported by BOTH the client (Review page pricing,
 * admin surfaces) and the server (Slice Pay sale price, push-to-trips), because
 * the one thing that must never drift is how many dollars a code takes off.
 *
 * ─── The one subtlety worth reading before touching this file ───────────────
 *
 * A code is advertised as a flat sum off the GRAND TOTAL — "$20 off with JAY20"
 * means the customer's all-in drops by exactly $20.00.
 *
 * But no financed rail bills the grand total. Every one of them bills the
 * PRE-hold subtotal and lets the 15% hold fee land on top:
 *
 *   Slice Pay        we POST `salePrice`; Slice mechanically adds its 15%
 *   Defera Capital   calculatePaymentSchedule({ totalWithFee }) adds holdFeeRate
 *   Defera Zero      same calculator, same shape
 *
 * So subtracting $20 from the subtotal would hand the customer $23.00 — the $20
 * plus the 15% hold that no longer accrues on it. Giving away an unbudgeted
 * 15% on every influencer redemption is exactly the kind of quiet leak that
 * only shows up in the ledger months later.
 *
 * The fix is to gross down instead: take $20 / 1.15 = $17.39 off the subtotal,
 * let the rail add its 15% to the reduced figure, and the all-in lands $20.00
 * lower. That is what preHoldDiscountCents() does, and it is why callers must
 * pass the hold rate rather than assume one.
 *
 * Pay-in-full paths (miles-only, Reserve) carry no hold fee. Pass holdFeeRate 0
 * and the discount applies at face value, which is already correct there.
 */

/**
 * Codes are stored and compared normalized. Mirrors the
 * `discount_codes_code_format` CHECK in migration 141 — keep the two in step.
 */
export const DISCOUNT_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,23}$/;

/** Query param that carries a code in from a creator's link-in-bio. */
export const DISCOUNT_REF_PARAM = 'ref';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Trim + uppercase. Returns '' for anything that cannot be a code, so callers
 * can treat falsy as "no code" without a second type check.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeDiscountCode(raw) {
  if (typeof raw !== 'string') return '';
  return raw.trim().toUpperCase();
}

/**
 * Normalize and shape-check in one step — the gate every entry point should use
 * before hitting the database, so malformed input never becomes a query.
 * @param {unknown} raw
 * @returns {string} the normalized code, or '' if it cannot be valid
 */
export function sanitizeDiscountCode(raw) {
  const code = normalizeDiscountCode(raw);
  return DISCOUNT_CODE_PATTERN.test(code) ? code : '';
}

/**
 * How many cents to take off the PRE-hold subtotal so that the customer's
 * post-hold grand total falls by exactly `discountCents`. See the file header.
 *
 * @param {number} discountCents - advertised discount off the grand total
 * @param {number} holdFeeRate - e.g. 0.15; pass 0 for pay-in-full paths
 * @returns {number} integer cents to remove from the pre-hold subtotal
 */
export function preHoldDiscountCents(discountCents, holdFeeRate = 0) {
  const cents = Math.max(0, Math.round(Number(discountCents) || 0));
  const rate = Number(holdFeeRate) || 0;
  if (cents === 0) return 0;
  return Math.round(cents / (1 + rate));
}

/**
 * Apply a discount to a pre-hold subtotal, clamped so it can never drive the
 * amount we bill below zero.
 *
 * The clamp matters: `min_trip_total_cents` on the code is the real guard, but
 * it is enforced at apply time against the total as it stood *then*. A customer
 * can afterwards remove a passenger, drop seats, or apply travel credits and
 * shrink the trip underneath a already-attached code. Rather than emit a
 * negative sale price to Slice Pay, we cap the discount at whatever is left and
 * report what was actually given.
 *
 * @param {number} subtotalBeforeHold - dollars, pre-hold (fare + fees + seats)
 * @param {number} discountCents - advertised discount off the grand total
 * @param {number} holdFeeRate - hold rate that will be applied downstream
 * @returns {{
 *   subtotalAfterDiscount: number,
 *   preHoldDiscount: number,
 *   grandTotalDiscount: number,
 *   clamped: boolean
 * }} dollars throughout; `grandTotalDiscount` is what the customer really saves
 *    and is what any receipt or report should quote.
 */
export function applyDiscountToSubtotal(subtotalBeforeHold, discountCents, holdFeeRate = 0) {
  const subtotal = Math.max(0, Number(subtotalBeforeHold) || 0);
  const rate = Number(holdFeeRate) || 0;
  const wanted = preHoldDiscountCents(discountCents, rate) / 100;
  const applied = Math.min(wanted, subtotal);

  return {
    subtotalAfterDiscount: round2(subtotal - applied),
    preHoldDiscount: round2(applied),
    grandTotalDiscount: round2(applied * (1 + rate)),
    clamped: applied < wanted,
  };
}

/**
 * Review's dollar reductions on a checkout's pricing, in order: a discount
 * code, then Defera Miles (supabase/functions/_shared/miles-redemption.js).
 * Each comes off the pre-hold subtotal grossed down by the same hold rate,
 * read once from the undiscounted pricing, so the total falls by each one's
 * value. The rate comes from what the calculator produced rather than from
 * the payment method: pay-in-full paths come back with holdFee 0 and take
 * both at face value.
 *
 * The checkout functions re-derive the price the same way (withDiscounts in
 * capital-checkout-integrity.js), and the parity tests run both over a grid.
 *
 * @param {object} base - calculateReviewPagePricing's result
 * @param {{ discountCents?: number, milesDiscountCents?: number }} reductions
 * @returns {object} `base` with subtotalBeforeHold, holdFee,
 *   grandTotalIncludingSeats and totalExcludingSeats reduced, plus
 *   `discountApplied`/`discountClamped` for a code and `milesApplied` for
 *   Miles (dollars off the grand total).
 */
export function applyCheckoutDiscounts(base, { discountCents = 0, milesDiscountCents = 0 } = {}) {
  const code = Math.max(0, Math.round(Number(discountCents) || 0));
  const miles = Math.max(0, Math.round(Number(milesDiscountCents) || 0));
  if (code === 0 && miles === 0) return base;

  const rate = base.subtotalBeforeHold > 0 ? base.holdFee / base.subtotalBeforeHold : 0;
  let subtotal = base.subtotalBeforeHold;
  let grand = base.grandTotalIncludingSeats;
  let excludingSeats = base.totalExcludingSeats;
  const out = { ...base };
  const take = (cents) => {
    const r = applyDiscountToSubtotal(subtotal, cents, rate);
    subtotal = r.subtotalAfterDiscount;
    grand = round2(grand - r.grandTotalDiscount);
    excludingSeats = round2(Math.max(0, excludingSeats - r.grandTotalDiscount));
    return r;
  };
  if (code > 0) {
    const r = take(code);
    out.discountApplied = r.grandTotalDiscount;
    out.discountClamped = r.clamped;
  }
  if (miles > 0) {
    out.milesApplied = take(miles).grandTotalDiscount;
  }
  return {
    ...out,
    subtotalBeforeHold: subtotal,
    holdFee: round2(subtotal * rate),
    grandTotalIncludingSeats: grand,
    totalExcludingSeats: excludingSeats,
  };
}

/**
 * Read the discount a cart is carrying out of `flight_data`.
 *
 * Deliberately does NOT look at `flight_data.promo_applied`. That flag is the
 * legacy service-fee waiver and is a completely separate effect — conflating
 * the two would silently stack an 11% waiver onto every dollar discount. See
 * the header of migration 141.
 *
 * @param {object|null|undefined} flightData - a cart's or booking's flight_data
 * @returns {{ code: string, codeId: string|null, cents: number, viaLink: boolean }|null}
 *   null when the cart carries no discount code.
 */
export function readCartDiscount(flightData) {
  const fd = flightData || {};
  const code = normalizeDiscountCode(fd.discount_code);
  const cents = Math.max(0, Math.round(Number(fd.discount_cents) || 0));
  if (!code || cents <= 0) return null;
  return {
    code,
    codeId: fd.discount_code_id || null,
    cents,
    viaLink: fd.discount_source === 'link',
  };
}

/**
 * Build the `flight_data` patch that stamps a code onto a cart. Kept here so
 * the field names live in exactly one place — the server writes them, the
 * client reads them, and readCartDiscount() above is the only reader.
 *
 * @param {{ code: string, codeId: string, cents: number, viaLink?: boolean }} d
 * @returns {object} spread onto the existing flight_data
 */
export function buildCartDiscountStamp({ code, codeId, cents, viaLink = false }) {
  return {
    discount_code: normalizeDiscountCode(code),
    discount_code_id: codeId,
    discount_cents: Math.round(cents),
    discount_source: viaLink ? 'link' : 'manual',
    discount_applied_at: new Date().toISOString(),
  };
}

/**
 * The discount fields to carry onto a BOOKING's flight_data.
 *
 * Distinct from buildCartDiscountStamp() above, which also stamps
 * `discount_applied_at` — that timestamp records when the customer attached the
 * code to their cart and must not be restamped when the booking is written.
 *
 * Every rail that creates a booking needs these fields on the row, because the
 * attribution reconciler matches on `flight_data.discount_code_id`. A rail that
 * drops them prices the trip correctly and then goes missing from the creator's
 * numbers.
 *
 * @param {{ code: string, codeId?: string|null, cents: number, viaLink?: boolean }|null} discount
 * @returns {object} empty when there is no discount, so it is always spreadable
 */
export function buildBookingDiscountFields(discount) {
  if (!discount || !discount.code || !(discount.cents > 0)) return {};
  return {
    discount_code: normalizeDiscountCode(discount.code),
    discount_code_id: discount.codeId ?? null,
    discount_cents: Math.round(discount.cents),
    discount_source: discount.viaLink ? 'link' : 'manual',
  };
}

/** Field names a cart carries for a discount — used to strip on removal. */
export const CART_DISCOUNT_FIELDS = Object.freeze([
  'discount_code',
  'discount_code_id',
  'discount_cents',
  'discount_source',
  'discount_applied_at',
]);

/**
 * Strip every discount field from a flight_data object, returning a new one.
 * @param {object|null|undefined} flightData
 * @returns {object}
 */
export function stripCartDiscount(flightData) {
  const next = { ...(flightData || {}) };
  for (const field of CART_DISCOUNT_FIELDS) delete next[field];
  return next;
}

/**
 * Format cents as a plain dollar string for UI and email copy.
 * @param {number} cents
 * @returns {string} e.g. "$20.00"
 */
export function formatDiscountAmount(cents) {
  const dollars = (Math.round(Number(cents) || 0)) / 100;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
